import {
  FreshExtraProduct,
  SubscriptionsAPI,
} from "@api-clients/subscriptions";
import { Allergen, SubscriptionStatus } from "@/primary-models";

import type { TFreshMealPlan } from "@api-clients/subscriptions";

export type NibblesPouchesUpsellEligibility =
  | { eligible: true; subscriptionId: number }
  | { eligible: false };

const POUCH_PRODUCTS = [
  FreshExtraProduct.NIBBLES_CHICKEN_POUCH,
  FreshExtraProduct.NIBBLES_SALMON_POUCH,
  FreshExtraProduct.NIBBLES_TUNA_POUCH,
];

const POUCH_ALLERGENS = [ Allergen.CHICKEN, Allergen.SALMON, Allergen.TUNA ];

/**
 * A plan already has the full Nibbles Trio Bundle if it has all three pouch
 * products present in its extras with a quantity greater than zero.
 */
function hasFullPouchBundle(plan: TFreshMealPlan): boolean {
  return POUCH_PRODUCTS.every((product) => plan.items.some(
    (item) => item.name === product && item.quantity > 0,
  ));
}

/**
 * A plan is ineligible if the cat on that subscription is allergic to any of
 * chicken, salmon, or tuna, since the bundle contains all three flavours.
 */
function hasRelevantAllergy(plan: TFreshMealPlan): boolean {
  const allergies = plan.cat?.allergies ?? [];
  return POUCH_ALLERGENS.some((allergen) => allergies.includes(allergen));
}

/**
 * Picks the most eligible plan: soonest `nextDelivery`, tie-broken by the
 * lowest subscription id. Mirrors the server-side `getMostEligibleSubscription`
 * ordering used for the existing single-flavour Nibbles upsell.
 */
function pickMostEligiblePlan(plans: TFreshMealPlan[]): TFreshMealPlan | undefined {
  return [ ...plans ].sort((a, b) => {
    const aTime = a.date.nextDelivery ? new Date(a.date.nextDelivery).getTime() : Infinity;
    const bTime = b.date.nextDelivery ? new Date(b.date.nextDelivery).getTime() : Infinity;

    if (aTime !== bTime) { return aTime - bTime; }

    return a.id - b.id;
  })[0];
}

/**
 * Determines whether the customer is eligible for the Nibbles Trio Bundle
 * (chicken, salmon, and tuna pouches) upsell, and if so, which subscription
 * the bundle should be applied to.
 *
 * Unlike the single-flavour Nibbles upsell, no discount eligibility logic is
 * performed here - the 50% discount is applied server-side ahead of time for
 * CRM-targeted customers. This check only determines whether it makes sense
 * to offer the bundle at all (active plan, no relevant allergies, doesn't
 * already have the bundle).
 *
 * @param subscriptionsAPI - An instance of the SubscriptionsAPI.
 * @returns `{ eligible: true, subscriptionId }` for the most eligible active
 * subscription, or `{ eligible: false }` if none qualify or the request fails.
 */
export async function checkNibblesPouchesUpsellEligibility(
  subscriptionsAPI: SubscriptionsAPI,
): Promise<NibblesPouchesUpsellEligibility> {
  try {
    const { freshMealPlans } = await subscriptionsAPI.getSubscriptionsOverview();

    const eligiblePlans = freshMealPlans.filter(
      (plan) => plan.status === SubscriptionStatus.ACTIVE
        && !hasRelevantAllergy(plan)
        && !hasFullPouchBundle(plan),
    );

    const mostEligiblePlan = pickMostEligiblePlan(eligiblePlans);

    if (!mostEligiblePlan) {
      return { eligible: false };
    }

    return { eligible: true, subscriptionId: mostEligiblePlan.id };
  } catch (error) {
    console.error("Error checking Nibbles pouches upsell eligibility", error);
    return { eligible: false };
  }
}
